import { useEffect, useState } from 'react';
import { getInflightCount, subscribeLoading } from './api';

// Minimum time the bar stays in the "loading" phase once a request has
// started, even if the response comes back within the same animation frame.
const MIN_SHOW_MS = 400;
// How long the "completing" phase lasts (bar at 100%, fading out).
const COMPLETE_MS = 320;

export type LoadingPhase = 'idle' | 'loading' | 'completing';

/**
 * Drives the top LoadingBar. While any API request is in flight the bar is in
 * the `loading` phase (growing from the left); when the last request settles it
 * moves to `completing` (fills to 100% and fades). Replaces per-screen skeleton
 * flicker — content stays mounted, this bar is the only loading affordance.
 */
export function useLoadingBar(): LoadingPhase {
  const [phase, setPhase] = useState<LoadingPhase>('idle');

  useEffect(() => {
    let minShowTimer: ReturnType<typeof setTimeout> | null = null;
    let completeTimer: ReturnType<typeof setTimeout> | null = null;
    let startedAt = 0;

    const start = () => {
      // A new request while completing: cancel the fade-out and keep loading.
      if (completeTimer) {
        clearTimeout(completeTimer);
        completeTimer = null;
      }
      if (minShowTimer) {
        clearTimeout(minShowTimer);
        minShowTimer = null;
      }
      if (startedAt !== 0) return; // already loading
      startedAt = Date.now();
      setPhase('loading');
    };

    const finish = () => {
      if (startedAt === 0) return; // not loading
      const remaining = Math.max(0, MIN_SHOW_MS - (Date.now() - startedAt));
      startedAt = 0;
      minShowTimer = setTimeout(() => {
        setPhase('completing');
        completeTimer = setTimeout(() => setPhase('idle'), COMPLETE_MS);
      }, remaining);
    };

    const unsubscribe = subscribeLoading(() => {
      if (getInflightCount() > 0) start();
      else finish();
    });

    return () => {
      unsubscribe();
      if (minShowTimer) clearTimeout(minShowTimer);
      if (completeTimer) clearTimeout(completeTimer);
    };
  }, []);

  return phase;
}
